import React from 'react';
import {
  SegmentGroup,
  type SegmentOption,
} from '../../../components/SegmentGroup';
import type { BaseInputProps } from '../../types/inputs';

export type { SegmentOption };

export interface SegmentGroupInputProps extends BaseInputProps<string> {
  options: SegmentOption[];
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

/**
 * Atomický segmentový přepínač pro formuláře — tenká nadstavba nad sdílenou
 * komponentou {@link SegmentGroup}, která drží vzhled i chování. Zde přibývá
 * jen napojení na `react-hook-form` přes {@link BaseInputProps}.
 *
 * Pojmenování skupiny řeší `aria-labelledby` na popisek z `<FormField />`
 * (stejný vzor jako {@link RadioGroupInput}).
 */
export const SegmentGroupInput: React.FC<SegmentGroupInputProps> = ({
  name,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  labelId,
  describedBy,
  testAttributes,
  options,
  orientation = 'horizontal',
  className,
}) => {
  const rootProps = React.useMemo(
    () => ({ name, ...testAttributes }),
    [name, testAttributes],
  );

  return (
    <SegmentGroup
      ref={ref}
      options={options}
      value={value ?? ''}
      onValueChange={onChange}
      orientation={orientation}
      disabled={disabled}
      readOnly={readOnly}
      invalid={hasError}
      onBlur={onBlur}
      className={className}
      aria-labelledby={labelId}
      aria-describedby={describedBy}
      data-testid={testAttributes['data-testid']}
      rootProps={rootProps}
    />
  );
};

SegmentGroupInput.displayName = 'SegmentGroupInput';
